"use client";

import { IconLayoutDashboard, IconLogout, IconMoon, IconSettings, IconSun } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { getImageProps } from "next/image";
import Link from "next/link";
import posthogClient from "posthog-js";
import { toast } from "sonner";
import { useTheme } from "@/hooks/use-theme";
import { useChatStore } from "@/lib/stores/chat-store";
import { signOut, useSession } from "@domainstack/auth/client";
import { Avatar, AvatarFallback, AvatarImage } from "@domainstack/ui/avatar";
import { Button } from "@domainstack/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@domainstack/ui/dropdown-menu";
export function UserMenu() {
  const queryClient = useQueryClient();
  const {
    data: session
  } = useSession();
  const {
    theme,
    toggleTheme
  } = useTheme();
  if (!session?.user) {
    return null;
  }
  const {
    user
  } = session;
  const avatarUrl = `/api/avatar/${user.id}`;
  const {
    props: imageProps
  } = getImageProps({
    src: avatarUrl,
    alt: user.name ?? "User avatar",
    width: 32,
    height: 32,
    loading: "eager",
    draggable: false
  });
  const initials = (user.name || "").split(" ").filter(n => n.length > 0).map(n_0 => n_0[0]).join("").toUpperCase().slice(0, 2) || "?";
  const handleSignOut = async () => {
    posthogClient.capture("sign_out_clicked");
    try {
      // Resolves `{ error }` on failure instead of throwing
      const {
        error
      } = await signOut({
        fetchOptions: {
          onSuccess: () => {
            // Drop everything the signed-in session loaded before leaving: the query
            // cache (domains, feed URL, …), the persisted chat, and — via a full-page
            // navigation — any routes Next preserved in <Activity>.
            queryClient.clear();
            useChatStore.getState().clearSession();
            window.location.assign("/");
          }
        }
      });
      if (error) {
        posthogClient.captureException(new Error(error.message ?? `sign-out failed (${error.status})`), {
          action: "sign_out"
        });
        toast.error("Couldn't sign you out. Please try again.");
      }
    } catch (err) {
      posthogClient.captureException(err, {
        action: "sign_out"
      });
      toast.error("Couldn't sign you out. Please try again.");
    }
  };
  return <DropdownMenu modal={false}>
      <DropdownMenuTrigger render={<Button variant="ghost" size="icon" className="rounded-full px-0 py-0 hover:bg-transparent active:scale-95 dark:hover:bg-transparent" aria-label="User menu">
            <Avatar className="size-8">
              <AvatarImage {...imageProps} />
              <AvatarFallback className="bg-muted text-xs text-muted-foreground">
                {initials}
              </AvatarFallback>
            </Avatar>
          </Button>} />
      <DropdownMenuContent align="end" className="min-w-56">
        <div className="flex items-center gap-[9px] p-1.5 select-none">
          <Avatar className="size-8">
            <AvatarImage {...imageProps} />
            <AvatarFallback className="bg-muted text-xs text-muted-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col space-y-[5px]">
            <p className="text-sm leading-none font-medium">{user.name || "User"}</p>
            <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
          </div>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem nativeButton={false} render={<Link href="/dashboard">
              <IconLayoutDashboard />
              Dashboard
            </Link>} />
        <DropdownMenuItem nativeButton={false} render={<Link href="/settings" scroll={false}>
              <IconSettings />
              Settings
            </Link>} />
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={toggleTheme} closeOnClick={false}>
          {theme === "dark" ? <IconSun /> : <IconMoon />}
          {theme === "dark" ? "Light mode" : "Dark mode"}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>
          <IconLogout className="text-danger-foreground" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>;
}